import { logger } from "@/lib/logger";

export type Path =
  | { type: "artifacts" }
  | {
      type: "iw-job-plan";
    }
  | {
      type: "iw";
      projectId: string;
      indexWorkerId?: string;
      isSettings?: boolean;
    }
  | {
      type: "sa";
      projectId: string;
      viewId?: string;
      isDefaultView?: boolean;
      isSettings?: boolean;
      isOnboarding?: boolean;
    }
  | {
      type: "ua";
      projectId: string;
      viewId?: string;
      isDefaultView?: boolean;
      isSettings?: boolean;
      isSessionExplorer?: boolean;
    }
  | {
      type: "ads";
      projectId: string;
      viewId?: string;
      isDefaultView?: boolean;
      isSettings?: boolean;
    }
  | {
      type: "ai";
    }
  | {
      type: "settings";
      isSCProperties?: boolean;
      isNotifications?: boolean;
      isSABigQuery?: boolean;
      isUABigQuery?: boolean;
      isWorkflowSkills?: boolean;
    }
  | {
      type: "dashboards";
      dashboardId: string;
    }
  | {
      type: "inbox";
    }
  | {
      type: "home";
    }
  | {
      type: "unknown";
    };

export function getPath(
  pathname: string,
  params: { [key: string]: string },
): Path {
  const segments = pathname.split("/");

  // Check if language segment is missing (development environment issue)
  const hasLangSegment = segments[1] && segments[1].length === 2; // e.g., "ja", "en"
  const offset = hasLangSegment ? 0 : -1; // Adjust offset if lang is missing

  if (segments[3 + offset] === "dashboards" && params.dashboardId) {
    return {
      type: "dashboards",
      dashboardId: decodeURIComponent(params.dashboardId),
    };
  }

  if (segments[3 + offset] === "artifacts") return { type: "artifacts" };

  if (segments[3 + offset] === "inbox") {
    return {
      type: "inbox",
    };
  }

  if (segments[3 + offset] === "ai") {
    return {
      type: "ai",
    };
  }

  if (segments[3 + offset] === "iw" && segments[4 + offset] === "job-plan") {
    return {
      type: "iw-job-plan",
    };
  }

  if (segments[3 + offset] === "iw" && params.projectId) {
    return {
      type: "iw",
      projectId: decodeURIComponent(params.projectId),
      indexWorkerId: params.iwId ? decodeURIComponent(params.iwId) : undefined,
      isSettings: segments[5 + offset] === "settings",
    };
  }

  if (segments[3 + offset] === "sa" && params.projectId) {
    return {
      type: "sa",
      projectId: decodeURIComponent(params.projectId),
      viewId: params.viewId ? decodeURIComponent(params.viewId) : undefined,
      isSettings: segments[5 + offset] === "settings",
      isOnboarding: segments[5 + offset] === "onboarding",
      isDefaultView: segments[5 + offset] === "default",
    };
  }

  if (segments[3 + offset] === "ua" && params.projectId) {
    return {
      type: "ua",
      projectId: decodeURIComponent(params.projectId),
      viewId: params.viewId ? decodeURIComponent(params.viewId) : undefined,
      isSettings: segments[5 + offset] === "settings",
      isDefaultView: segments[5 + offset] === "default",
      isSessionExplorer: segments[5 + offset] === "session_explorer",
    };
  }

  if (segments[3 + offset] === "ads" && params.projectId) {
    return {
      type: "ads",
      projectId: decodeURIComponent(params.projectId),
      viewId: params.viewId ? decodeURIComponent(params.viewId) : undefined,
      isSettings: segments[5 + offset] === "settings",
      isDefaultView: segments[5 + offset] === "default",
    };
  }

  if (segments[3 + offset] === "settings") {
    return {
      type: "settings",
      isSCProperties: segments[4 + offset] === "sc-properties",
      isNotifications: segments[4 + offset] === "notifications",
      isSABigQuery: segments[4 + offset] === "sa-bigquery",
      isUABigQuery: segments[4 + offset] === "ua-bigquery",
      isWorkflowSkills: segments[4 + offset] === "workflow-skills",
    };
  }

  if (segments[3 + offset] === undefined) {
    return {
      type: "home",
    };
  }

  logger.warn(
    `Unknown path detected: ${pathname}. Please check the implementation of getPath.`,
    { pathname, params },
  );

  return {
    type: "unknown",
  };
}
